import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/storeHooks";
import { ROUTE_PATHS } from "../routes";
import { REDUX_REQUEST_STATUS } from "../types/commonTypes";
import { TermsStatusEnum, TermsViewerModeEnum } from "../types/termsTypes";
import {
	acceptTermsThunk,
	fetchCurrentTermsThunk,
	fetchTermsContentThunk,
	fetchTermsVersionThunk,
	remindSuperAdminsThunk,
} from "../store/terms/termsThunk";
import { closeTermsViewer, openTermsViewer } from "../store/terms/termsSlice";
import { useUserContext } from "./UserContext";
import { useOnVendorUpdated } from "../hooks/firestoreHooks/vendorUpdated";

interface ITermsModalContext {
	/** True only for an explicit `blocked`. Never inferred from a missing status. */
	showBlockingModal: boolean;
	/** True only for an explicit `notice`. */
	showNoticeBanner: boolean;
	viewerMode: TermsViewerModeEnum | null;
	openNoticeViewer: () => void;
	openBlockingViewer: () => void;
	openReadOnlyViewer: () => void;
	closeViewer: () => void;
	acceptTerms: () => Promise<void>;
	remindSuperAdmin: () => void;
	logout: () => void;
}

const TermsModalContext = createContext<ITermsModalContext | null>(null);

export const useTermsModalContext = (): ITermsModalContext => {
	const context = useContext(TermsModalContext);
	if (!context) {
		throw new Error("useTermsModalContext must be used within a TermsModalContextProvider");
	}
	return context;
};

interface ITermsModalContextProviderProps {
	children: ReactNode;
}

/** Matches Baqaal's CURRENT_TERMS_TTL_MS — refetching sooner just re-reads its cache. */
const REFETCH_THROTTLE_MS = 60_000;

const TermsModalContextProvider: React.FC<ITermsModalContextProviderProps> = ({ children }) => {
	const dispatch = useAppDispatch();
	const { logout } = useUserContext();
	const { isLoggedIn, userDetails } = useAppSelector((state) => state.user);
	const canCheckTerms = isLoggedIn && !userDetails?.mustChangePassword;
	const { current, viewerMode } = useAppSelector((state) => state.terms);

	const lastFetchedAtRef = React.useRef(0);

	const refreshCurrentTerms = useCallback(
		(force = false) => {
			const now = Date.now();
			if (!force && now - lastFetchedAtRef.current < REFETCH_THROTTLE_MS) {
				return;
			}
			lastFetchedAtRef.current = now;
			dispatch(fetchCurrentTermsThunk());
		},
		[dispatch],
	);

	useEffect(() => {
		if (!canCheckTerms) {
			lastFetchedAtRef.current = 0;
			return;
		}

		refreshCurrentTerms(true);

		// A tab left open overnight can cross the effective date with no other
		// signal — the 403 interceptor only helps once a request is made, and only
		// once BAQ-1797 registers the guard. Re-checking on focus closes that gap
		// without polling.
		const onFocus = () => {
			if (document.visibilityState === "visible") {
				refreshCurrentTerms();
			}
		};

		window.addEventListener("focus", onFocus);
		document.addEventListener("visibilitychange", onFocus);

		return () => {
			window.removeEventListener("focus", onFocus);
			document.removeEventListener("visibilitychange", onFocus);
		};
	}, [canCheckTerms, refreshCurrentTerms]);

	/*
	 * The Super Admin accepting lifts the block for the whole team, but nothing
	 * else tells a colleague's tab. Baqaal bumps `updatedAt` on every team
	 * member's Firestore doc when an acceptance lands, so refetching here closes
	 * the blocking sheet — or clears the notice banner — without a manual reload.
	 *
	 * Forced past the throttle: the signal means something actually changed, and
	 * making someone sit behind a sheet for up to another minute would defeat the
	 * point. `updatedAt` is a generic "something about you changed" flag that other
	 * flows may bump too, so this stays a single cheap GET and nothing more.
	 */
	useOnVendorUpdated(
		useCallback(() => {
			refreshCurrentTerms(true);
		}, [refreshCurrentTerms]),
	);

	const loadContentFor = useCallback(
		(version: string | undefined) => {
			if (version) {
				dispatch(fetchTermsContentThunk(version));
			}
		},
		[dispatch],
	);

	const openNoticeViewer = useCallback(() => {
		loadContentFor(current?.version);
		dispatch(openTermsViewer(TermsViewerModeEnum.NOTICE));
	}, [current?.version, dispatch, loadContentFor]);

	const openBlockingViewer = useCallback(() => {
		loadContentFor(current?.version);
		dispatch(openTermsViewer(TermsViewerModeEnum.BLOCKING));
	}, [current?.version, dispatch, loadContentFor]);

	/**
	 * The sidebar link. Shows the version the vendor actually agreed to, which is
	 * what governs them — falling back to the current one for a vendor who has
	 * never accepted. Superseded snapshots keep answering, so this stays valid
	 * after a new version supersedes theirs.
	 *
	 * Also fetches that version's own `effectiveDate` — `current.effectiveDate`
	 * is the *live* published version's date, which is wrong once this is a
	 * superseded snapshot the vendor already accepted under different terms.
	 */
	const openReadOnlyViewer = useCallback(() => {
		const version = current?.acceptedVersion ?? current?.version;
		loadContentFor(version);
		if (version) {
			dispatch(fetchTermsVersionThunk(version));
		}
		dispatch(openTermsViewer(TermsViewerModeEnum.READ_ONLY));
	}, [current?.acceptedVersion, current?.version, dispatch, loadContentFor]);

	const closeViewer = useCallback(() => {
		dispatch(closeTermsViewer());
	}, [dispatch]);

	const acceptTerms = useCallback(async () => {
		if (!current?.version) {
			return;
		}

		const resp = await dispatch(acceptTermsThunk(current.version));

		// The accept response carries only `{ version, acceptedAt }`, so the new
		// state has to come from the server. Never synthesize `accepted` here.
		if (resp.meta.requestStatus === REDUX_REQUEST_STATUS.FULFILLED) {
			refreshCurrentTerms(true);
		}
	}, [current?.version, dispatch, refreshCurrentTerms]);

	const remindSuperAdmin = useCallback(() => {
		dispatch(remindSuperAdminsThunk());
	}, [dispatch]);

	const handleLogout = useCallback(() => {
		logout(ROUTE_PATHS.LOGIN);
	}, [logout]);

	const contextValue = useMemo<ITermsModalContext>(
		() => ({
			// Both surfaces key on an explicit status. With no terms published the
			// request 404s, `current` stays null and nothing renders — the same
			// fail-open behaviour as the server guard. Testing `!== 'accepted'`
			// here would block every vendor in the portal.
			showBlockingModal: current?.status === TermsStatusEnum.BLOCKED,
			showNoticeBanner: current?.status === TermsStatusEnum.NOTICE,
			viewerMode,
			openNoticeViewer,
			openBlockingViewer,
			openReadOnlyViewer,
			closeViewer,
			acceptTerms,
			remindSuperAdmin,
			logout: handleLogout,
		}),
		[
			current?.status,
			viewerMode,
			openNoticeViewer,
			openBlockingViewer,
			openReadOnlyViewer,
			closeViewer,
			acceptTerms,
			remindSuperAdmin,
			handleLogout,
		],
	);

	return <TermsModalContext.Provider value={contextValue}>{children}</TermsModalContext.Provider>;
};

export default TermsModalContextProvider;
